import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import {
	Card,
	CardBody,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	UncontrolledDropdown,
	UncontrolledTooltip,
} from "reactstrap";
import { getDisconnectReasonKey } from "../../../Integrations/HiBob/messages";

// HiBob's official logo, from their partner brand assets. Until the file is
// there (or if it fails to load) the tile shows "Bob" as text.
export const HIBOB_LOGO_SRC = "/assets/images/integrations/hibob.svg";

export function BrandTile() {
	const [failed, setFailed] = useState(false);

	if (failed) {
		return (
			<div className="hibob-brand" aria-hidden="true">
				Bob
			</div>
		);
	}
	return (
		<div className="hibob-brand has-logo" aria-hidden="true">
			<img src={HIBOB_LOGO_SRC} alt="" onError={() => setFailed(true)} />
		</div>
	);
}

const STATUS = {
	connected: { key: "hibob-status-connected", tone: "is-success" },
	disconnected: { key: "hibob-status-disconnected", tone: "is-warning" },
};

/** Dot + text, never colour only. The disconnect reason is a tooltip and a screen reader description. */
export function StatusBadge({ status, reason }) {
	const { t } = useTranslation();
	const { key, tone } = STATUS[status] || { key: "hibob-status-not-connected", tone: "is-neutral" };
	const reasonText = status === "disconnected" ? t(getDisconnectReasonKey(reason)) : null;

	return (
		<>
			<span
				id="hibob-status-badge"
				className={`hibob-status ${tone}`}
				tabIndex={reasonText ? 0 : undefined}
				aria-describedby={reasonText ? "hibob-status-reason" : undefined}
			>
				<span className="hibob-dot" aria-hidden="true" />
				{t(key)}
			</span>
			{reasonText && (
				<>
					<span id="hibob-status-reason" className="sr-only">
						{reasonText}
					</span>
					<UncontrolledTooltip target="hibob-status-badge">{reasonText}</UncontrolledTooltip>
				</>
			)}
		</>
	);
}

function SyncActions({ running, syncing, availableAt, formats, onSyncNow, onDisconnect }) {
	const { t } = useTranslation();
	const busy = running || syncing;
	const disabled = busy || !!availableAt;

	return (
		<div className="hibob-actions mt-3 mt-lg-0 ml-lg-auto">
			<div className="d-flex align-items-center">
				<button
					type="button"
					className="btn btn-primary flex-grow-1 flex-lg-grow-0 mr-2"
					onClick={onSyncNow}
					disabled={disabled}
					aria-describedby={availableAt && !busy ? "hibob-available-at" : undefined}
				>
					<FontAwesomeIcon icon={faArrowsRotate} spin={busy} className="mr-2" aria-hidden="true" />
					{busy ? t("hibob-syncing") : t("hibob-sync-now")}
				</button>
				<UncontrolledDropdown>
					<DropdownToggle
						color="secondary"
						className="px-3 border"
						aria-label={t("hibob-more-actions")}
						title={t("hibob-more-actions")}
					>
						<FontAwesomeIcon icon={faEllipsisVertical} aria-hidden="true" />
					</DropdownToggle>
					<DropdownMenu right>
						<DropdownItem className="hibob-danger-text" onClick={onDisconnect}>
							{t("hibob-disconnect-menu")}
						</DropdownItem>
					</DropdownMenu>
				</UncontrolledDropdown>
			</div>
			{availableAt && !busy && (
				<div id="hibob-available-at" className="hibob-muted text-sm mt-1">
					{t("hibob-available-at", { time: formats.when(availableAt) })}
				</div>
			)}
		</div>
	);
}

function Header({ status, reason, showActions, ...actionProps }) {
	const { t } = useTranslation();

	return (
		<Card>
			<CardBody className="d-flex flex-column flex-lg-row align-items-lg-center">
				<div className="d-flex align-items-center">
					<BrandTile />
					<div className="ml-3">
						<div className="d-flex align-items-center flex-wrap">
							<h1 className="h2 mb-0 mr-3">{t("hibob")}</h1>
							<StatusBadge status={status} reason={reason} />
						</div>
						<p className="hibob-muted text-sm mb-0 mt-1">{t("hibob-subtitle")}</p>
					</div>
				</div>
				{showActions && <SyncActions {...actionProps} />}
			</CardBody>
		</Card>
	);
}

export default Header;
